import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

@customElement('sat-top-bar')
export class SatTopBar extends LitElement {
  /** True while the ≤500px hamburger drawer is open. */
  @state() private mobileOpen = false;

  private outsideClickHandler = (e: MouseEvent): void => {
    if (!this.mobileOpen) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.mobileOpen = false;
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.outsideClickHandler);
  }

  disconnectedCallback(): void {
    document.removeEventListener('click', this.outsideClickHandler);
    super.disconnectedCallback();
  }

  static styles = css`
    :host {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.6rem 1rem;
      background: var(--color-bg-overlay);
      border-bottom: 1px solid var(--color-border);
      backdrop-filter: blur(8px);
      z-index: 10;
      /* Anchor for the mobile drawer at ≤500px. */
      position: relative;
    }

    .brand {
      display: flex;
      align-items: baseline;
      gap: 0.6rem;
      font-family: var(--font-mono);
      flex-shrink: 0;
    }
    .brand__glyph {
      color: var(--color-accent);
      font-size: 1.4rem;
      line-height: 1;
    }
    .brand__name {
      color: var(--color-text);
      font-size: 1rem;
      font-weight: 500;
    }
    /* Hint that the brand wrapper carries a tooltip-on-hover. */
    .brand[title] {
      cursor: help;
    }

    nav {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      color: var(--color-text-muted);
      font-family: var(--font-mono);
      font-size: 0.85rem;
    }
    nav .divider {
      color: var(--color-text-dim);
    }
    nav .item {
      cursor: pointer;
      color: inherit;
      text-decoration: none;
    }
    nav .item:hover {
      color: var(--color-accent);
      text-decoration: none;
    }
    nav .item--placeholder {
      opacity: 0.4;
      cursor: default;
    }
    nav .item--placeholder:hover {
      color: var(--color-text-muted);
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 0.8rem;
      flex-shrink: 0;
    }

    /* Hamburger button — hidden by default; flipped on below 500px. */
    .hamburger {
      display: none;
      background: transparent;
      color: var(--color-text);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 0.3rem 0.55rem;
      font-family: var(--font-mono);
      font-size: 1.1rem;
      line-height: 1;
      cursor: pointer;
    }
    .hamburger:hover { border-color: var(--color-accent); color: var(--color-accent); }
    .hamburger[aria-expanded='true'] { border-color: var(--color-accent); color: var(--color-accent); }

    /* Responsive collapse — keep the dividers between nav links intact at
       every breakpoint above 500px. The tagline is no longer in the bar
       (moved to a hover tooltip on the brand wrapper); only the brand
       name + action-pill labels are progressively hidden. Action-pill
       label collapse below 1300px lives inside each pill component.
       Below 500px everything except the brand glyph + hamburger
       collapses into a drawer. */
    @media (max-width: 1500px) {
      .brand__name { display: none; }
    }
    @media (max-width: 800px) {
      nav { display: none; }
    }
    @media (max-width: 500px) {
      :host {
        flex-wrap: wrap;
        padding: 0.5rem 0.75rem;
      }
      .hamburger { display: inline-flex; }

      /* nav + actions move into a full-width drawer below the bar, hidden
         until the hamburger flips :host([data-mobile-open]). */
      nav,
      .actions {
        display: none;
      }
      :host([data-mobile-open]) nav,
      :host([data-mobile-open]) .actions {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        gap: 0.5rem;
        padding: 0.6rem 0;
      }
      :host([data-mobile-open]) nav { padding-top: 0.6rem; }
      :host([data-mobile-open]) .actions {
        border-top: 1px dashed var(--color-border);
        margin-top: 0.4rem;
      }
      /* Dividers don't make sense in a vertical list. */
      :host([data-mobile-open]) nav .divider { display: none; }
      :host([data-mobile-open]) nav .item {
        padding: 0.55rem 0.4rem;
        font-size: 1rem;
      }
      /* Let pills grow to the row width so taps are easy. */
      :host([data-mobile-open]) .actions > * {
        width: 100%;
      }
    }
  `;

  private toggleMobile(e: Event): void {
    e.stopPropagation();
    this.mobileOpen = !this.mobileOpen;
  }

  private closeMobile(): void {
    if (this.mobileOpen) this.mobileOpen = false;
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('mobileOpen')) {
      if (this.mobileOpen) this.setAttribute('data-mobile-open', '');
      else this.removeAttribute('data-mobile-open');
    }
  }

  render() {
    return html`
      <div class="brand" title="Space situational awareness, legible">
        <span class="brand__glyph" aria-hidden="true">⊕</span>
        <span class="brand__name">sat.trackr.live</span>
      </div>
      <button
        class="hamburger"
        type="button"
        aria-label="Menu"
        aria-expanded=${this.mobileOpen ? 'true' : 'false'}
        @click=${this.toggleMobile}
      >☰</button>
      <nav aria-label="Primary" @click=${this.closeMobile}>
        <a class="item" href="/text">Catalog</a>
        <span class="divider" aria-hidden="true">·</span>
        <a class="item" href="/text/launches">Launches</a>
        <span class="divider" aria-hidden="true">·</span>
        <a class="item" href="/text/decays">Decays</a>
        <span class="divider" aria-hidden="true">·</span>
        <a class="item" href="/text/conjunctions">Conjunctions</a>
        <span class="divider" aria-hidden="true">·</span>
        <a class="item" href="/text/stats">Stats</a>
        <span class="divider" aria-hidden="true">·</span>
        <a class="item" href="/text/events">Events</a>
      </nav>
      <div class="actions">
        <sat-search></sat-search>
        <sat-observer-pill></sat-observer-pill>
        <sat-view-toggle></sat-view-toggle>
        <sat-space-weather-pill></sat-space-weather-pill>
        <sat-overlays-menu></sat-overlays-menu>
        <sat-share-button></sat-share-button>
        <sat-zoom-button></sat-zoom-button>
        <sat-settings></sat-settings>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-top-bar': SatTopBar;
  }
}
